/**
 * Format a `Date` as `YYYY-MM-DD` using **local** calendar fields.
 *
 * `toISOString()` converts to UTC first, which shifts the calendar day for
 * users in negative-offset timezones (e.g. UTC−5 around midnight). Search
 * filters and defaults (`new Date()`, `setDate()`, date-picker output) are
 * all local-time `Date` objects, so URLs and API params must encode them in
 * the same frame.
 */
export function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format an **instant** as the German display date `DD.MM.YYYY`.
 *
 * `offsetMinutes` is the UTC offset the instant was authored in — shift by it,
 * then read UTC fields, so the calendar day matches what the source intended
 * regardless of where the viewer sits. DaVinci due dates arrive as local
 * midnight with their offset attached (`/Date(1790287200000+0200)/` = 25.09.),
 * so passing the offset from {@link tryParseDaVinciDate} is what keeps the date
 * from rendering a day early. Defaults to `0` (plain UTC fields).
 *
 * Distinct from the store's `checkInFormatted`, which renders a 2-digit year.
 */
export function formatDayMonthYear(date: Date, offsetMinutes = 0): string {
  const shifted = new Date(date.getTime() + offsetMinutes * 60_000);
  const day = String(shifted.getUTCDate()).padStart(2, '0');
  const month = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${shifted.getUTCFullYear()}`;
}

/**
 * Convert a calendar-date input to a `Date` anchored at **UTC midnight**.
 *
 * Use when handing a calendar date (no time) to an API that serializes via
 * `.getTime()` (DaVinci `/Date(ms)/`) or otherwise treats the timestamp as a
 * UTC instant. Plain `new Date('2026-05-23T00:00:00')` parses TZ-naive strings
 * as **local** time, which produces the wrong UTC instant for any non-UTC
 * client (e.g. CEST: 23.5. 00:00 local → 22.5. 22:00 UTC → server reads 22.5.).
 *
 * Accepts:
 * - `'YYYY-MM-DD'` or `'YYYY-MM-DDTHH:mm:ss[.sss][Z]'` strings — only the date
 *   portion is honoured; any time/zone suffix is ignored.
 * - `Date` objects — the **local** Y/M/D fields are read (i.e. the calendar
 *   day the user saw), preserving the intended date when the input came from
 *   a date-picker.
 *
 * A string carrying `Z` or a `±HH:mm` offset is an INSTANT, not a calendar date —
 * its UTC date part may be the previous day (e.g. a local-midnight Date piped
 * through `toISOString()`). That is a caller bug (fix at the source, typically
 * with {@link formatDateLocal}); it is logged so it can't slip through silently.
 */
export function toUtcMidnight(input: string | Date): Date {
  if (typeof input === 'string') {
    if (/(?:Z|[+-]\d{2}:?\d{2})$/.test(input)) {
      console.warn(
        `[dateFormatter] toUtcMidnight received an instant string ("${input}") — ` +
          `its UTC date part may be off by one day. Pass a calendar date ` +
          `('YYYY-MM-DD' / TZ-naive string / Date object) instead.`
      );
    }
    const [y, m, d] = input.split('T')[0].split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d));
  }
  return new Date(Date.UTC(input.getFullYear(), input.getMonth(), input.getDate()));
}
